/**
 * Storefront loading.
 *
 * One hook resolves whatever the customer arrived with — a table token from a
 * printed QR, or a restaurant slug from a link — into the two things the menu
 * screen needs: the restaurant's policy and its menu.
 *
 * Both come from server RPCs that already resolve effective prices, discount
 * windows, sold-out state, and time-of-day availability, so nothing here
 * re-derives a pricing or availability rule.
 *
 * The result is kept for a short while. Checkout is a separate route, so the
 * menu unmounts on the way there and remounts on the way back — and without a
 * cache every return re-resolved the scan and re-fetched the restaurant and the
 * whole menu, blanking the screen while it did. A menu does change under a
 * customer's feet — a dish sells out, a price moves — so the copy is short-lived
 * and any load older than it is refreshed, with what is on screen left in place
 * until the fresh copy arrives.
 */

import { useRef } from 'react';

import type { PublicMenu, RestaurantPublic, ScanResolution } from '@yummy/shared';
import { fetchMenu, fetchRestaurant, resolveScan } from '@yummy/db';
import { useAsync } from '@yummy/ui-rnr';

import { supabase } from '../lib/supabase';

export interface Storefront {
  resolution: ScanResolution;
  restaurant: RestaurantPublic;
  menu: PublicMenu;
}

export interface StorefrontSource {
  /** A `YMT1` table token or a restaurant slug. */
  value: string;
  kind: 'token' | 'slug';
}

/**
 * How long a loaded storefront may answer a fresh screen.
 *
 * Long enough that walking to checkout and back never re-fetches, short enough
 * that a menu cannot be badly out of date: the customer sees a dish that sold
 * out in the meantime only if they go back to it within half a minute.
 */
const CACHE_TTL_MS = 30_000;

/**
 * The last storefront per source, for this tab.
 *
 * Module-level rather than in a provider because every screen that needs a
 * storefront needs the same one, and none of them outlives the others.
 */
const cache = new Map<string, { storefront: Storefront; at: number }>();

/**
 * The restaurant on its own, by slug.
 *
 * The menu and checkout both need it, and they need the same one — but checkout
 * reaches it by slug rather than by whatever the customer arrived with, and it
 * also needs pickup slots, which are worth re-reading every time. Caching the
 * restaurant and not the slots keeps the stale copy to the part that only
 * changes when the merchant changes it.
 */
const restaurants = new Map<string, { restaurant: RestaurantPublic; at: number }>();

/**
 * Loads a restaurant, briefly reusing the last copy.
 *
 * @param slug The restaurant's slug.
 * @returns The restaurant, or null when it is gone.
 */
export async function loadRestaurant(slug: string): Promise<RestaurantPublic | null> {
  const hit = restaurants.get(slug);
  if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.restaurant;

  const restaurant = await fetchRestaurant(supabase, slug);
  if (restaurant) restaurants.set(slug, { restaurant, at: Date.now() });
  return restaurant;
}

/**
 * Forgets everything loaded for a restaurant.
 *
 * Called once an order is placed: the next screen should see the stock the
 * order just consumed rather than the copy that still says it is available.
 *
 * @param slug The restaurant's slug.
 */
export function forgetStorefront(slug: string): void {
  restaurants.delete(slug);
  for (const [key, entry] of cache) {
    if (entry.storefront.restaurant.slug === slug) cache.delete(key);
  }
}

/**
 * Loads the restaurant and menu for a scan or a slug.
 *
 * @param source What the customer arrived with.
 * @returns Loading state and the resolved storefront.
 */
export function useStorefront(source: StorefrontSource) {
  const key = `${source.kind}:${source.value}`;

  // Which key this instance has already answered from the cache. A mount may be
  // served from it; a `reload()` — the retry button — must not be, or the button
  // would do nothing for half a minute.
  const servedFromCache = useRef<string | null>(null);

  const state = useAsync<Storefront>(async () => {
    const hit = cache.get(key);
    const fresh = hit !== undefined && Date.now() - hit.at < CACHE_TTL_MS;
    if (fresh && servedFromCache.current !== key) {
      servedFromCache.current = key;
      return hit.storefront;
    }

    const resolution = await resolveScan(supabase, source.value);

    const [restaurant, menu] = await Promise.all([
      loadRestaurant(resolution.slug),
      fetchMenu(supabase, resolution.slug),
    ]);

    if (!restaurant) {
      throw new Error('That restaurant is no longer available.');
    }
    if (!menu) {
      throw new Error('That menu is not available right now.');
    }

    const storefront = { resolution, restaurant, menu };
    cache.set(key, { storefront, at: Date.now() });
    return storefront;
  }, [key]);

  // What is on screen while the fetch runs: the previous copy when there is one,
  // so returning to the menu shows the menu rather than a spinner. `initial` is
  // what the screens check before blanking themselves.
  const shown = state.data ?? cache.get(key)?.storefront ?? null;

  return {
    ...state,
    data: shown,
    initial: state.initial && shown === null,
  };
}

/**
 * The table token for the current visit, if the customer scanned a sticker.
 *
 * Persisted so the attribution survives the navigation to checkout, which is a
 * separate route.
 *
 * @param token The token, when one was scanned.
 */
export function rememberTableToken(token: string | null): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    if (token) sessionStorage.setItem('yummy.customer.table', token);
    else sessionStorage.removeItem('yummy.customer.table');
  } catch {
    // Private browsing can block storage; table attribution is a nice-to-have.
  }
}

/**
 * Reads the table token remembered for this visit.
 *
 * @returns The token, or null.
 */
export function recallTableToken(): string | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    return sessionStorage.getItem('yummy.customer.table');
  } catch {
    return null;
  }
}
